import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';
import { jitteredPollMs, jitteredStaleTime } from '~/utils/jitter';

import { SCHEDULES_OVERVIEW_POLL_MS, refetchOnShow } from '../constants/polling';
import {
  getSchedule,
  getScheduleTree,
  getScheduleTriggerOptions,
  getSchedulesOverview,
  getSchedulesTree,
  listScheduleFeed,
  listScheduleFirings,
  listSchedules,
} from '../services/schedules.service';
import type { ScheduleFeedFilters, SchedulesListFilters } from '../types/schedules.types';

/**
 * The schedules dashboard (counts, 30-day window, upcoming, needs a person). Refreshes ~30 s only
 * while the tab is visible; a poll missed while hidden runs once on show (each read asks Temporal).
 */
export function useSchedulesOverview() {
  return useQuery({
    queryKey: queryKeys.catalogService.schedules.overview(),
    queryFn: getSchedulesOverview,
    refetchInterval: (query) => jitteredPollMs(query, SCHEDULES_OVERVIEW_POLL_MS),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: (query) =>
      refetchOnShow(query.state.dataUpdatedAt, SCHEDULES_OVERVIEW_POLL_MS),
  });
}

/** One page of the schedule list; keeps the previous page on screen while paging/filtering. */
export function useSchedulesList(params: SchedulesListFilters & { page: number; size: number }) {
  return useQuery({
    queryKey: queryKeys.catalogService.schedules.list({ ...params }),
    queryFn: () => listSchedules(params),
    placeholderData: keepPreviousData,
  });
}

/** One schedule + Temporal's live view; idle until a schedule is selected. */
export function useScheduleDetail(scheduleId: string | null) {
  return useQuery({
    queryKey: queryKeys.catalogService.schedules.detail(scheduleId ?? ''),
    queryFn: () => getSchedule(scheduleId ?? ''),
    enabled: !!scheduleId,
  });
}

/** One schedule's firing history, paged. */
export function useScheduleFirings(scheduleId: string | null, page: number, size: number) {
  return useQuery({
    queryKey: queryKeys.catalogService.schedules.firings(scheduleId ?? '', { page, size }),
    queryFn: () => listScheduleFirings(scheduleId ?? '', { page, size }),
    enabled: !!scheduleId,
    placeholderData: keepPreviousData,
  });
}

/** Tenant-wide firings feed, paged and filtered. */
export function useScheduleFeed(params: ScheduleFeedFilters & { page: number; size: number }) {
  return useQuery({
    queryKey: queryKeys.catalogService.schedules.feed({ ...params }),
    queryFn: () => listScheduleFeed(params),
    placeholderData: keepPreviousData,
  });
}

/**
 * Trigger options for the create form (all connectors — empty connector filter).
 * Cached long: the option set only changes with a catalog-service deploy.
 */
export function useScheduleTriggerOptions() {
  return useQuery({
    queryKey: queryKeys.catalogService.triggerOptions(''),
    queryFn: getScheduleTriggerOptions,
    staleTime: jitteredStaleTime(10 * 60_000),
  });
}

/** Tenant-wide execution tree: the 20 most recently touched schedules' newest firings. */
export function useSchedulesTree() {
  return useQuery({
    queryKey: queryKeys.catalogService.schedules.tree(),
    queryFn: getSchedulesTree,
  });
}

/** One schedule's execution tree (its newest 20 firings, nested run nodes); idle until opened. */
export function useScheduleTree(scheduleId: string | null) {
  return useQuery({
    queryKey: queryKeys.catalogService.schedules.scheduleTree(scheduleId ?? ''),
    queryFn: () => getScheduleTree(scheduleId ?? ''),
    enabled: !!scheduleId,
  });
}
